import { X, Trophy, RefreshCw, Scissors, MessageCircle, Sparkles, Lock } from 'lucide-react';
import { useEntitlements } from '@hooks/useEntitlements';

const MODES = [
  {
    id: 'squiggle',
    name: 'Squiggle Game',
    description: 'One person scribbles, another turns it into something. Invented by a child therapist in the 1960s.',
    icon: Sparkles,
    iconBg: 'bg-emerald-500/10',
    iconHoverBg: 'group-hover:bg-emerald-500/20',
    iconColour: 'text-emerald-500',
    freeTier: true,
  },
  {
    id: 'corpse',
    name: 'Exquisite Corpse',
    description: 'Draw head, body, or legs without seeing the rest. Reveal the creature together. A Surrealist classic from 1925.',
    icon: Scissors,
    iconBg: 'bg-violet-500/10',
    iconHoverBg: 'group-hover:bg-violet-500/20',
    iconColour: 'text-violet-500',
    freeTier: true,
  },
  {
    id: 'challenge',
    name: 'Challenge Mode',
    description: "Everyone draws the same thing on their own canvas. Vote for the best. Who's the top artist?",
    icon: Trophy,
    iconBg: 'bg-amber/10',
    iconHoverBg: 'group-hover:bg-amber/20',
    iconColour: 'text-amber',
    freeTier: false,
  },
  {
    id: 'relay',
    name: 'Relay Mode',
    description: 'Take turns building one canvas together. Watch the drawing emerge as each player adds to it.',
    icon: RefreshCw,
    iconBg: 'bg-amber/10',
    iconHoverBg: 'group-hover:bg-amber/20',
    iconColour: 'text-amber',
    freeTier: false,
  },
  {
    id: 'telephone',
    name: 'Broken Telephone',
    description: 'Draw a prompt. Next person guesses what it is. Next person draws that guess. Watch it drift.',
    icon: MessageCircle,
    iconBg: 'bg-sky-500/10',
    iconHoverBg: 'group-hover:bg-sky-500/20',
    iconColour: 'text-sky-500',
    freeTier: false,
  },
];

/**
 * Modal for host to select a game mode.
 * Premium modes are locked behind Host Pass; clicking one opens the paywall.
 */
export default function GameModeSelector({ onSelect, onClose }) {
  const { hasHostPass, canUsePremiumGameMode, openPaywall } = useEntitlements();

  const handleClick = (mode) => {
    if (canUsePremiumGameMode(mode.id)) {
      onSelect(mode.id);
    } else {
      openPaywall('premium-game-mode');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="w-full max-w-sm bg-white rounded-2xl shadow-xl overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 shrink-0">
          <div>
            <h2 className="text-lg font-semibold text-charcoal font-display">Start a Game</h2>
            {!hasHostPass && (
              <p className="text-[11px] text-slate-400 font-display mt-0.5">
                2 free modes · 3 more with Host Pass
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-charcoal hover:bg-slate-100 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-3 overflow-y-auto">
          {MODES.map((mode) => {
            const locked = !mode.freeTier && !hasHostPass;
            const Icon = mode.icon;
            return (
              <button
                key={mode.id}
                onClick={() => handleClick(mode)}
                className={`w-full text-left px-4 py-4 rounded-xl border transition-all group relative ${
                  locked
                    ? 'border-slate-100 bg-slate-50/50 hover:border-amber/40 hover:bg-amber/5'
                    : 'border-slate-200 hover:border-amber/40 hover:bg-amber/5'
                }`}
              >
                {locked && (
                  <div className="absolute top-2 right-2 flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber/10 text-amber text-[9px] font-mono uppercase tracking-wider font-semibold">
                    <Lock className="w-2.5 h-2.5" />
                    Host Pass
                  </div>
                )}
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-lg ${mode.iconBg} flex items-center justify-center shrink-0 ${mode.iconHoverBg} transition-colors ${locked ? 'opacity-60' : ''}`}>
                    <Icon className={`w-5 h-5 ${mode.iconColour}`} />
                  </div>
                  <div className={locked ? 'opacity-60' : ''}>
                    <p className="text-sm font-semibold text-charcoal font-display pr-12">
                      {mode.name}
                    </p>
                    <p className="text-xs text-slate-400 mt-0.5 leading-relaxed">
                      {mode.description}
                    </p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
